// Documents: one library of the venue's menus, recipes, SOPs and training files.
import { venuePeople } from '@/src/lib/venuePeople';
// Files live in three storage buckets; folders (document_folders) organise them and can be locked to some people.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Upload, Loader2, Search, Check, AlertTriangle, ChevronLeft, ChevronRight, Download, RefreshCw, Trash2, Pencil, ArrowRight, X, Sparkles, ListChecks, GraduationCap, Folder, FolderPlus, Lock, Users, FileText } from 'lucide-react';
import { useDocFolders, type DocFolder, type FolderAccess } from '@/src/hooks/useDocFolders';
import { openEodPdf } from '@/src/lib/eodReportFile';
import { lazy, Suspense } from 'react';
import { useVenueDocs, type VenueDoc } from '@/src/hooks/useVenueDocs';
import { FilePlus2, Plus, PenLine } from 'lucide-react';
import PdfQuickEdit, { type PdfChange } from './pdf-quick-edit';
const DocEditor = lazy(() => import('./doc-editor'));
import { useFileUpload, type BucketId, type UploadedFile } from '@/src/hooks/useFileUpload';
import { useAppColors } from '@/src/lib/theme';
import { supabase } from '@/src/lib/supabase';
import { DOC_TYPES, docTypeLabel } from '@/src/lib/documentTypes';
import { PdfPages } from './pdf-pages';

interface DocumentsPageProps { venueId: string; canManage: boolean; userId: string; userName: string; openDocId?: string | null; onOpenedDoc?: () => void;
  /** From chat: open this PDF in Quick edit with these changes ready. */
  pdfEdit?: { bucket: string; name: string; changes: PdfChange[] } | null; onPdfEditOpened?: () => void }

type Doc = UploadedFile & { bucket: BucketId; label: string; ext: string; type: string | null; folderId: string | null };
// Where you are: a folder, everything at the top level (null), or the "needs a look" list.
type Place = string | null | 'look';
type Group = { id: string; label: string; types: string[]; grid: boolean };

const GROUPS: Group[] = [
  { id: 'menus', label: 'Menus', types: ['a_la_carte', 'set_menu', 'dietary_menu', 'drinks_menu', 'allergens', 'menu'], grid: true },
  { id: 'recipes', label: 'Recipes', types: ['food_recipe', 'beverage_recipe'], grid: true },
  { id: 'sops', label: 'SOPs and policies', types: ['sop'], grid: false },
  { id: 'service', label: 'Service', types: ['sequence_of_service'], grid: false },
  { id: 'training', label: 'Training', types: ['training'], grid: false },
  { id: 'venue', label: 'Venue guide', types: ['venue_guide'], grid: false },
  { id: 'other', label: 'Other', types: ['other'], grid: false },
];
// Where an upload goes, by type (menus and recipes are public for the menu images; the rest private).
const bucketFor = (type: string, file: File): BucketId =>
  file.type.startsWith('video/') || type === 'training' ? 'training-videos'
    : GROUPS[0].types.includes(type) || GROUPS[1].types.includes(type) ? 'menu-images' : 'sops';

const cleanLabel = (name: string) => name.replace(/^\d{10,}-/, '').replace(/\.[a-z0-9]+$/i, '').replace(/[_]+/g, ' ').trim();
const extOf = (name: string) => (name.split('.').pop() ?? '').toLowerCase();
const isImage = (e: string) => ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic'].includes(e);
const isVideo = (e: string) => ['mp4', 'mov', 'webm', 'm4v'].includes(e);
const formatAud = (n: number) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
const shortDate = (iso: string) => (iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '');

interface Usage { courses: Array<{ id: string; title: string }>; tasks: Array<{ id: string; title: string; done: number; total: number }> }

export default function DocumentsPage({ venueId, canManage, userId, userName, openDocId, onOpenedDoc, pdfEdit, onPdfEditOpened }: DocumentsPageProps) {
  const c = useAppColors();
  const sops = useFileUpload('sops', venueId);
  const training = useFileUpload('training-videos', venueId);
  const menus = useFileUpload('menu-images', venueId);
  const hooks = useMemo(() => ({ sops, 'training-videos': training, 'menu-images': menus } as Record<string, ReturnType<typeof useFileUpload>>), [sops, training, menus]);

  const [types, setTypes] = useState<Record<string, string>>({});
  const [aiStatus, setAiStatus] = useState<Record<string, { status: string; text: string | null }>>({});
  const [usage, setUsage] = useState<Record<string, Usage>>({});
  const [q, setQ] = useState('');
  const [place, setPlace] = useState<Place>(null);
  const fx = useDocFolders(venueId, canManage);
  const vd = useVenueDocs(venueId);
  const [docOpen, setDocOpen] = useState<string | null>(null);
  // Opened from a chat link ("Open the doc").
  useEffect(() => { if (openDocId) { setDocOpen(openDocId); onOpenedDoc?.(); } }, [openDocId, onOpenedDoc]);
  const [folderOf, setFolderOf] = useState<Record<string, string | null>>({});
  const [dialog, setDialog] = useState<null | { kind: 'new' } | { kind: 'rename'; folder: DocFolder } | { kind: 'access'; folder: DocFolder }>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const loading = sops.loading || training.loading || menus.loading;
  const fileCount = sops.files.length + training.files.length + menus.files.length;
  const key = (bucket: string, path: string) => `${bucket}:${path}`;

  // Types, what the AI has read, and where each file is used.
  const loadMeta = useCallback(async () => {
    const [{ data: meta }, { data: txt }, { data: mods }, { data: tks }] = await Promise.all([
      supabase.from('document_meta').select('*').eq('venue_id', venueId),
      supabase.from('document_text').select('bucket, path, status, text').eq('venue_id', venueId),
      supabase.from('training_modules').select('id, course_id, source_docs, training_courses(title)').eq('venue_id', venueId),
      supabase.from('tasks').select('id, title, link, status, task_assignees(completed_at)').eq('venue_id', venueId).eq('link->>type', 'document'),
    ]);
    const t: Record<string, string> = {};
    const fo: Record<string, string | null> = {};
    for (const r of (meta ?? []) as Array<{ bucket: string; path: string; doc_type: string; folder_id?: string | null }>) { t[key(r.bucket, r.path)] = r.doc_type; fo[key(r.bucket, r.path)] = r.folder_id ?? null; }
    setTypes(t);
    setFolderOf(fo);
    const s: Record<string, { status: string; text: string | null }> = {};
    for (const r of (txt ?? []) as Array<{ bucket: string; path: string; status: string; text: string | null }>) s[key(r.bucket, r.path)] = { status: r.status, text: r.text };
    setAiStatus(s);
    const u: Record<string, Usage> = {};
    const get = (k: string) => (u[k] ??= { courses: [], tasks: [] });
    for (const m of (mods ?? []) as Array<{ course_id: string; source_docs?: Array<{ bucket: string; name: string }> | null; training_courses: { title: string } | Array<{ title: string }> | null }>) {
      const title = (Array.isArray(m.training_courses) ? m.training_courses[0]?.title : m.training_courses?.title) ?? 'Course';
      for (const d of m.source_docs ?? []) {
        const list = get(key(d.bucket, `${venueId}/${d.name}`)).courses;
        if (!list.some((x) => x.id === m.course_id)) list.push({ id: m.course_id, title });
      }
    }
    for (const tk of (tks ?? []) as Array<{ id: string; title: string; status: string; link: { bucket: string; name: string } | null; task_assignees?: Array<{ completed_at: string | null }> }>) {
      if (!tk.link || tk.status === 'cancelled') continue;
      const a = tk.task_assignees ?? [];
      get(key(tk.link.bucket, `${venueId}/${tk.link.name}`)).tasks.push({ id: tk.id, title: tk.title, done: a.filter((x) => x.completed_at).length, total: a.length });
    }
    setUsage(u);
  }, [venueId]);
  useEffect(() => { void loadMeta(); }, [loadMeta, fileCount]);
  // The AI reads new uploads in the background; check back while any are being read.
  useEffect(() => {
    if (!Object.values(aiStatus).some((s) => s.status === 'processing')) return;
    const t = window.setTimeout(() => void loadMeta(), 8000);
    return () => window.clearTimeout(t);
  }, [aiStatus, loadMeta]);

  const docs: Doc[] = useMemo(() => {
    const all: Doc[] = [];
    for (const [bucket, h] of Object.entries(hooks)) {
      for (const f of h.files) {
        if (f.name.startsWith('.')) continue;
        const fid = folderOf[key(bucket, f.path)] ?? null;
        all.push({ ...f, bucket: bucket as BucketId, label: cleanLabel(f.name), ext: extOf(f.name), type: types[key(bucket, f.path)] ?? null, folderId: fid && fx.folders.some((x) => x.id === fid) ? fid : null });
      }
    }
    return all.sort((a, b) => a.label.localeCompare(b.label));
  }, [hooks, types, folderOf, fx.folders]);

  // Files per folder, counting everything inside subfolders too.
  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const d of docs) for (const f of fx.pathOf(d.folderId)) m[f.id] = (m[f.id] ?? 0) + 1;
    for (const d of vd.docs) for (const f of fx.pathOf(d.folder_id)) m[f.id] = (m[f.id] ?? 0) + 1;
    return m;
  }, [docs, vd.docs, fx]);
  const needsLook = docs.filter((d) => aiStatus[key(d.bucket, d.path)]?.status === 'failed');
  const searching = !!q.trim();
  const folderId = place === 'look' ? null : place;
  const current = folderId ? fx.folders.find((f) => f.id === folderId) ?? null : null;
  const isReports = current?.system_key === 'reports';
  const subfolders = place === 'look' || searching ? [] : fx.folders.filter((f) => f.parent_id === folderId)
    .sort((a, b) => Number(a.system_key === 'reports') - Number(b.system_key === 'reports') || a.position - b.position || a.name.localeCompare(b.name));
  const shown = docs.filter((d) => searching
    ? `${d.label} ${docTypeLabel(d.type) ?? ''}`.toLowerCase().includes(q.trim().toLowerCase())
    : place === 'look' ? needsLook.includes(d) : d.folderId === folderId);
  const where = (d: Doc) => fx.pathOf(d.folderId).map((f) => f.name).join(' › ') || 'Not in a folder';
  // OpsAlo docs (written in the editor) in this folder, or matching the search.
  const shownDocs: VenueDoc[] = place === 'look' ? [] : vd.docs.filter((d) => searching
    ? d.title.toLowerCase().includes(q.trim().toLowerCase())
    : (d.folder_id && fx.folders.some((f) => f.id === d.folder_id) ? d.folder_id : null) === folderId);
  const newDoc = async () => {
    const r = await vd.create('Untitled', isReports ? null : folderId, userId);
    if (r.error) setErr(r.error); else if (r.id) setDocOpen(r.id);
  };
  // If the open folder disappears (deleted, or access changed), go back to the top.
  useEffect(() => { if (folderId && fx.ready && !fx.folders.some((f) => f.id === folderId)) setPlace(null); }, [folderId, fx.ready, fx.folders]);
  const open = openKey ? docs.find((d) => key(d.bucket, d.path) === openKey) ?? null : null;
  const [editPreset, setEditPreset] = useState<PdfChange[] | null>(null);
  useEffect(() => {
    if (!pdfEdit) return;
    const d = docs.find((x) => x.bucket === pdfEdit.bucket && (x.name === pdfEdit.name || x.path.endsWith(`/${pdfEdit.name}`)));
    if (!d) return; // the list is still loading; try again when it has
    setOpenKey(key(d.bucket, d.path)); setEditPreset(pdfEdit.changes); onPdfEditOpened?.();
  }, [pdfEdit, docs]); // eslint-disable-line react-hooks/exhaustive-deps

  // New uploads get read by the AI straight away (instead of on the first chat question), and the
  // page refreshes while any are still being read.
  const startReading = useCallback(() => {
    if (!canManage) return;
    void supabase.functions.invoke('onboarding-docs', { body: { mode: 'read' } }).then(() => { setTimeout(() => void loadMeta(), 1500); });
  }, [canManage, loadMeta]);
  const unreadCount = docs.filter((d) => { const st = aiStatus[key(d.bucket, d.path)]?.status; return st !== 'ready' && st !== 'failed' && /\.(pdf|png|jpe?g|webp|gif|md|txt|csv|json)$/i.test(d.path); }).length;
  useEffect(() => {
    if (!unreadCount) return;
    const id = window.setInterval(() => void loadMeta(), 10_000);
    return () => window.clearInterval(id);
  }, [unreadCount, loadMeta]);
  const kicked = useRef(false);
  useEffect(() => { if (unreadCount && !kicked.current) { kicked.current = true; startReading(); } }, [unreadCount, startReading]);

  const setType = async (d: Doc, docType: string) => {
    setTypes((p) => ({ ...p, [key(d.bucket, d.path)]: docType }));
    const { error } = await supabase.from('document_meta').upsert({ venue_id: venueId, bucket: d.bucket, path: d.path, doc_type: docType, updated_at: new Date().toISOString() }, { onConflict: 'bucket,path' });
    if (error) setErr(/document_meta/.test(error.message) ? 'Document types need a quick database update first.' : error.message);
  };

  const uploadFiles = async (files: File[], docType: string) => {
    setErr(null);
    const into = isReports ? null : folderId;
    for (const f of files) {
      const b = bucketFor(docType, f);
      const { error, path } = await hooks[b].upload(f);
      if (error) { setErr(error); return false; }
      if (path) {
        let r = await supabase.from('document_meta').upsert({ venue_id: venueId, bucket: b, path, doc_type: docType, folder_id: into, updated_at: new Date().toISOString() }, { onConflict: 'bucket,path' });
        if (r.error && /folder_id/.test(r.error.message)) r = await supabase.from('document_meta').upsert({ venue_id: venueId, bucket: b, path, doc_type: docType, updated_at: new Date().toISOString() }, { onConflict: 'bucket,path' });
      }
    }
    await loadMeta();
    startReading();
    return true;
  };

  if (docOpen) {
    return (
      <div className="h-full flex flex-col">
        <Suspense fallback={<div className="flex justify-center py-24"><Loader2 size={20} className="animate-spin" style={{ color: c.textFaint }} /></div>}>
          <DocEditor docId={docOpen} venueId={venueId} userId={userId} userName={userName}
            onClose={() => { setDocOpen(null); void vd.reload(); }} onDeleted={() => { setDocOpen(null); void vd.reload(); }} />
        </Suspense>
      </div>
    );
  }

  if (open) {
    return <DocDetail doc={open} venueId={venueId} canManage={canManage} hook={hooks[open.bucket]} editPreset={editPreset} onPresetUsed={() => setEditPreset(null)} ai={aiStatus[key(open.bucket, open.path)] ?? null}
      folders={fx.folders.filter((f) => f.system_key !== 'reports')} folderLabel={(id) => fx.pathOf(id).map((f) => f.name).join(' › ')}
      onMove={async (fid) => { const r = await fx.moveDoc(open.bucket, open.path, fid, open.type); if (!r.error) await loadMeta(); return r.error; }}
      usage={usage[key(open.bucket, open.path)] ?? { courses: [], tasks: [] }}
      onBack={() => setOpenKey(null)} onType={(t) => void setType(open, t)}
      onReplaced={async (file) => {
        const t = open.type ?? 'other';
        const b = bucketFor(t, file);
        const { error, path } = await hooks[b].upload(file);
        if (error || !path) return error ?? 'Upload failed';
        await supabase.from('document_meta').upsert({ venue_id: venueId, bucket: b, path, doc_type: t, updated_at: new Date().toISOString() }, { onConflict: 'bucket,path' });
        if (open.folderId) await fx.moveDoc(b, path, open.folderId, t);
        await hooks[open.bucket].remove(open.path);
        await loadMeta();
        startReading();
        setOpenKey(key(b, path));
        return null;
      }}
      onDeleted={async () => { await hooks[open.bucket].remove(open.path); setOpenKey(null); await loadMeta(); }}
      onRenamed={async (name) => { const r = await hooks[open.bucket].rename(open.path, name); if (!r.error) { setOpenKey(null); await loadMeta(); } return r.error ?? null; }} />;
  }

  const crumbs = fx.pathOf(folderId);
  const topFolders = fx.folders.filter((f) => !f.parent_id).sort((a, b) => Number(a.system_key === 'reports') - Number(b.system_key === 'reports') || a.position - b.position || a.name.localeCompare(b.name));
  const looseCount = docs.filter((d) => !d.folderId).length;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="px-4 sm:px-8 py-6 max-w-6xl mx-auto w-full space-y-5 pb-24">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold tracking-tight" style={{ color: c.text }}>Documents</h1>
          <p className="text-sm" style={{ color: c.textDim }}>Menus, recipes, SOPs and reports. Your team and OpsAlo's AI read from here.</p>
        </div>
        <div className="flex gap-2 flex-wrap w-full sm:w-auto">
          <label className="flex items-center gap-2 h-11 px-3.5 rounded-xl flex-1 sm:w-72" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
            <Search size={16} style={{ color: c.textDim }} />
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all documents" aria-label="Search documents"
              className="flex-1 min-w-0 bg-transparent outline-none text-sm" style={{ color: c.text }} />
          </label>
          {canManage && !isReports && (
            <>
              <button onClick={() => setDialog({ kind: 'new' })} className="h-11 px-3.5 rounded-xl text-sm font-semibold flex items-center gap-2" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
                <FolderPlus size={16} /> New folder
              </button>
              <button onClick={() => void newDoc()} className="h-11 px-3.5 rounded-xl text-sm font-semibold flex items-center gap-2" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
                <FilePlus2 size={16} /> New doc
              </button>
              <button onClick={() => setUploadOpen(true)} className="h-11 px-4 rounded-xl text-sm font-semibold flex items-center gap-2" style={{ background: c.accent, color: '#fff' }}>
                <Upload size={16} /> Upload
              </button>
            </>
          )}
        </div>
      </header>

      {(err || fx.error) && <p className="text-sm" style={{ color: c.danger }}>{err ?? fx.error}</p>}

      <div className="flex gap-6 items-start">
        <nav aria-label="Folders" className="hidden md:flex flex-col gap-0.5 w-56 shrink-0">
          <SideLink label="All documents" n={docs.length} on={place === null && !searching} onClick={() => { setPlace(null); setQ(''); }} />
          {topFolders.map((f) => (
            <SideLink key={f.id} label={f.name} n={f.system_key === 'reports' ? undefined : counts[f.id] ?? 0} lock={fx.isLocked(f.id)}
              on={!searching && !!folderId && (folderId === f.id || fx.pathOf(folderId).some((x) => x.id === f.id))} onClick={() => { setPlace(f.id); setQ(''); }} />
          ))}
          {needsLook.length > 0 && (
            <>
              <div className="h-px my-2 mx-1" style={{ background: c.border }} />
              <SideLink label="Needs a look" n={needsLook.length} on={place === 'look'} onClick={() => { setPlace('look'); setQ(''); }} warn />
            </>
          )}
        </nav>

        <main className="flex-1 min-w-0 space-y-5">
          {/* Phone: top folders as chips */}
          <div className="md:hidden flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 scrollbar-hide">
            {[{ id: null as string | null, name: 'All' }, ...topFolders].map((f) => (
              <button key={f.id ?? 'all'} onClick={() => { setPlace(f.id); setQ(''); }} className="h-9 px-3.5 rounded-full text-[13px] font-semibold whitespace-nowrap flex items-center gap-1.5"
                style={(f.id === null ? place === null : !!folderId && (folderId === f.id || fx.pathOf(folderId).some((x) => x.id === f.id))) ? { background: c.text, color: c.bg } : { background: c.bgElevated, color: c.text, border: `1px solid ${c.border}` }}>
                {f.id && fx.isLocked(f.id) && <Lock size={11} />}{f.name}
              </button>
            ))}
          </div>

          {/* Where you are + folder actions */}
          {!searching && place !== 'look' && current && (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <nav aria-label="Folder path" className="flex items-center gap-1 text-sm flex-wrap min-w-0">
                <button onClick={() => setPlace(null)} style={{ color: c.textDim }}>All documents</button>
                {crumbs.map((f, i) => (
                  <span key={f.id} className="flex items-center gap-1 min-w-0">
                    <ChevronRight size={14} style={{ color: c.textFaint }} />
                    <button onClick={() => setPlace(f.id)} className="truncate flex items-center gap-1" style={{ color: i === crumbs.length - 1 ? c.text : c.textDim, fontWeight: i === crumbs.length - 1 ? 700 : 500 }}>
                      {f.restricted && <Lock size={12} />}{f.name}
                    </button>
                  </span>
                ))}
              </nav>
              {canManage && (
                <div className="flex gap-1.5">
                  <button onClick={() => setDialog({ kind: 'access', folder: current })} className="h-9 px-3 rounded-lg text-[13px] font-semibold flex items-center gap-1.5" style={{ background: c.iconBg, color: c.text }}>
                    <Users size={14} /> {accessSummary(current, fx.access, fx.isLocked(current.parent_id))}
                  </button>
                  <button onClick={() => setDialog({ kind: 'rename', folder: current })} className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: c.iconBg, color: c.text }} aria-label="Rename folder"><Pencil size={14} /></button>
                  {!isReports && (
                    <button onClick={async () => {
                      const n = counts[current.id] ?? 0;
                      const up = current.parent_id ? fx.folders.find((f) => f.id === current.parent_id)?.name ?? 'the folder above' : 'All documents';
                      if (!window.confirm(`Delete the folder "${current.name}"?${n ? ` Its ${n} file${n === 1 ? '' : 's'} and any subfolders move to ${up}${current.restricted ? ', where more people may see them' : ''}.` : ''} No files are deleted.`)) return;
                      const r = await fx.remove(current.id); if (r.error) setErr(r.error); else { setPlace(current.parent_id); await loadMeta(); }
                    }} className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: c.iconBg, color: c.danger }} aria-label="Delete folder"><Trash2 size={14} /></button>
                  )}
                </div>
              )}
            </div>
          )}

          {canManage && needsLook.length > 0 && place !== 'look' && !searching && !current && (
            <div className="flex items-center gap-3 rounded-2xl px-4 py-3" style={{ background: c.isDark ? 'rgba(232,160,32,0.14)' : '#FCEFD9' }}>
              <AlertTriangle size={18} className="shrink-0" style={{ color: c.isDark ? '#FCD38D' : '#8A4B08' }} />
              <p className="text-sm flex-1" style={{ color: c.isDark ? '#FCD38D' : '#5C3205' }}>
                The AI couldn't read <b>{needsLook[0].label}</b>{needsLook.length > 1 ? ` and ${needsLook.length - 1} more` : ''}. Replace it with a clear PDF or photo so staff questions about it get answered.
              </p>
              <button onClick={() => setOpenKey(key(needsLook[0].bucket, needsLook[0].path))} className="h-9 px-3 rounded-lg text-[13px] font-semibold shrink-0" style={{ background: c.bgElevated, color: c.text }}>Open</button>
            </div>
          )}

          {isReports ? <ReportsView venueId={venueId} /> : (loading || !fx.ready) && docs.length === 0 ? (
            <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin" style={{ color: c.textFaint }} /></div>
          ) : (
            <>
              {subfolders.length > 0 && (
                <section className="space-y-2.5">
                  {!current && <h2 className="text-[12px] font-bold uppercase tracking-[0.06em]" style={{ color: c.textDim }}>Folders</h2>}
                  <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 xl:grid-cols-4">
                    {subfolders.map((f) => (
                      <button key={f.id} onClick={() => setPlace(f.id)} className="text-left rounded-2xl p-4 flex flex-col gap-2" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
                        <span className="flex items-center justify-between">
                          <Folder size={22} style={{ color: f.system_key === 'reports' ? c.gold : c.accentSoft }} />
                          {fx.isLocked(f.id) && <Lock size={13} style={{ color: c.textDim }} />}
                        </span>
                        <span className="text-[15px] font-bold truncate" style={{ color: c.text }}>{f.name}</span>
                        <span className="text-[12px]" style={{ color: c.textDim }}>
                          {f.system_key === 'reports' ? 'Nightly & weekly PDFs' : `${counts[f.id] ?? 0} file${(counts[f.id] ?? 0) === 1 ? '' : 's'}`}
                          {canManage && f.restricted ? ` · ${accessSummary(f, fx.access, false)}` : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {shown.length + shownDocs.length > 0 ? (
                <section className="space-y-2.5">
                  {(subfolders.length > 0 || searching) && (
                    <h2 className="text-[12px] font-bold uppercase tracking-[0.06em]" style={{ color: c.textDim }}>
                      {searching ? `${shown.length + shownDocs.length} result${shown.length + shownDocs.length === 1 ? '' : 's'}` : current ? 'Files' : `Not in a folder · ${looseCount + shownDocs.length}`}
                    </h2>
                  )}
                  <div className="rounded-2xl overflow-hidden" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
                    {shownDocs.map((d, i) => (
                      <div key={d.id} className="flex items-center" style={{ borderTop: i ? `1px solid ${c.border}` : undefined }}>
                      <button onClick={() => setDocOpen(d.id)} className="flex-1 min-w-0 text-left flex items-center gap-3.5 pl-4 pr-2 py-3.5">
                        <span className="rounded-[10px] flex items-center justify-center shrink-0" style={{ width: 36, height: 36, background: c.accentBg, color: c.accentSoft }}><FileText size={17} /></span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-[15px] font-semibold truncate" style={{ color: c.text }}>{d.title}</span>
                          <span className="block text-[13px] truncate" style={{ color: c.textDim }}>
                            {[searching ? (fx.pathOf(d.folder_id).map((f) => f.name).join(' › ') || 'Not in a folder') : null, 'OpsAlo doc', d.ai_created ? 'Drafted by AI' : null, d.general_access === 'restricted' ? 'Shared with some people' : null].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        <span className="text-[13px] shrink-0" style={{ color: c.textDim }}>{shortDate(d.updated_at)}</span>
                      </button>
                      {(canManage || d.created_by === userId) && (
                        <button aria-label={`Delete ${d.title}`} title="Delete" className="h-10 w-10 mr-2 rounded-lg flex items-center justify-center shrink-0" style={{ color: c.textDim }}
                          onClick={async () => {
                            if (!window.confirm(`Delete "${d.title}"? Everyone loses access to it and this can't be undone.`)) return;
                            const r = await vd.remove(d.id); if (r.error) setErr(r.error);
                          }}>
                          <Trash2 size={15} />
                        </button>
                      )}
                      </div>
                    ))}
                    {shown.map((d, i) => <DocRow key={key(d.bucket, d.path)} d={d} first={i === 0 && shownDocs.length === 0} ai={aiStatus[key(d.bucket, d.path)]?.status} use={usage[key(d.bucket, d.path)]}
                      where={searching || place === 'look' ? where(d) : null} onOpen={() => setOpenKey(key(d.bucket, d.path))} />)}
                  </div>
                </section>
              ) : subfolders.length === 0 && (
                <div className="rounded-2xl p-10 text-center" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
                  <p className="text-base font-semibold" style={{ color: c.text }}>{searching ? 'Nothing matches that search' : current ? 'This folder is empty' : 'No documents yet'}</p>
                  <p className="text-sm mt-1" style={{ color: c.textDim }}>{searching ? 'Try another word.' : canManage ? 'Upload files here, or make a folder inside it.' : 'Nothing here yet.'}</p>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {uploadOpen && <UploadModal onClose={() => setUploadOpen(false)} onUpload={uploadFiles} into={current?.name ?? null}
        initialType={({ menus: 'a_la_carte', recipes: 'food_recipe', sops: 'sop', training: 'training', service: 'sequence_of_service', venue: 'venue_guide', other: 'other' } as Record<string, string>)[crumbs[0]?.system_key ?? ''] ?? 'a_la_carte'} />}

      {dialog?.kind === 'new' && (
        <NameDialog title={current ? `New folder in ${current.name}` : 'New folder'} initial="" onClose={() => setDialog(null)}
          onSave={async (name) => { const r = await fx.create(name, folderId); if (r.error) return r.error; setDialog(null); if (r.id) setPlace(r.id); return null; }}
          note={current && fx.isLocked(current.id) ? `It starts with the same access as ${current.name}.` : 'Everyone can see it until you change who has access.'} />
      )}
      {dialog?.kind === 'rename' && (
        <NameDialog title="Rename folder" initial={dialog.folder.name} onClose={() => setDialog(null)}
          onSave={async (name) => { const r = await fx.rename(dialog.folder.id, name); if (r.error) return r.error; setDialog(null); return null; }} />
      )}
      {dialog?.kind === 'access' && (
        <AccessDialog venueId={venueId} folder={dialog.folder} access={fx.access.filter((a) => a.folder_id === dialog.folder.id)}
          parentLocked={fx.isLocked(dialog.folder.parent_id)} onClose={() => setDialog(null)}
          onSave={async (restricted, roles, users) => { const r = await fx.setFolderAccess(dialog.folder.id, restricted, roles, users); if (r.error) return r.error; setDialog(null); return null; }} />
      )}
    </motion.div>
  );
}

const TEAMS: Array<{ role: string; label: string }> = [
  { role: 'staff', label: 'Floor staff' }, { role: 'chef', label: 'Chefs' }, { role: 'bartender', label: 'Bartenders' },
];
function accessSummary(f: DocFolder, access: FolderAccess[], parentLocked: boolean): string {
  if (!f.restricted) return parentLocked ? 'Same as above' : 'Everyone';
  const mine = access.filter((a) => a.folder_id === f.id);
  const teams = mine.filter((a) => a.role).map((a) => TEAMS.find((t) => t.role === a.role)?.label ?? a.role);
  const people = mine.filter((a) => a.user_id).length;
  const parts = [...teams, ...(people ? [`${people} ${people === 1 ? 'person' : 'people'}`] : [])];
  return parts.length ? `Managers + ${parts.join(', ')}` : 'Managers only';
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  const c = useAppColors();
  return (
    <>
      <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-[60]" onClick={onClose} />
      <div role="dialog" aria-label={title} className="modal-panel fixed inset-x-4 lg:inset-x-auto lg:left-1/2 lg:-translate-x-1/2 lg:w-[480px] top-16 max-h-[calc(100vh-8rem)] rounded-2xl z-[60] flex flex-col overflow-hidden" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
        <header className="flex items-center justify-between px-5 py-4" style={{ borderBottom: `1px solid ${c.border}` }}>
          <h2 className="text-base font-bold" style={{ color: c.text }}>{title}</h2>
          <button onClick={onClose} className="p-2 -m-1" style={{ color: c.textDim }} aria-label="Close"><X size={18} /></button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">{children}</div>
      </div>
    </>
  );
}

function NameDialog({ title, initial, note, onClose, onSave }: { title: string; initial: string; note?: string; onClose: () => void; onSave: (n: string) => Promise<string | null> }) {
  const c = useAppColors();
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <ModalShell title={title} onClose={onClose}>
      <form onSubmit={async (e) => { e.preventDefault(); if (!name.trim()) return; setBusy(true); const r = await onSave(name); setBusy(false); if (r) setErr(r); }} className="space-y-3">
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Folder name, e.g. Kitchen"
          className="w-full h-11 px-3 rounded-xl text-sm outline-none" style={{ background: c.bgElevated, border: `1px solid ${c.borderStrong}`, color: c.text }} />
        {note && <p className="text-[12px]" style={{ color: c.textDim }}>{note}</p>}
        {err && <p className="text-sm" style={{ color: c.danger }}>{err}</p>}
        <button disabled={busy || !name.trim()} className="w-full h-11 rounded-xl text-sm font-semibold disabled:opacity-50" style={{ background: c.accent, color: '#fff' }}>{busy ? 'Saving…' : 'Save'}</button>
      </form>
    </ModalShell>
  );
}

function AccessDialog({ venueId, folder, access, parentLocked, onClose, onSave }: {
  venueId: string; folder: DocFolder; access: FolderAccess[]; parentLocked: boolean; onClose: () => void;
  onSave: (restricted: boolean, roles: string[], userIds: string[]) => Promise<string | null>;
}) {
  const c = useAppColors();
  const isReports = folder.system_key === 'reports';
  const [restricted, setRestricted] = useState(folder.restricted);
  const [roles, setRoles] = useState<string[]>(access.filter((a) => a.role).map((a) => a.role as string));
  const [userIds, setUserIds] = useState<string[]>(access.filter((a) => a.user_id).map((a) => a.user_id as string));
  const [people, setPeople] = useState<Array<{ id: string; name: string; app_role: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    void venuePeople(venueId)
      .then((data) => setPeople(data.filter((u) => u.app_role !== 'owner' && u.app_role !== 'manager').map((u) => ({ id: u.id, app_role: u.app_role, name: u.name ?? 'Team member' }))));
  }, [venueId]);
  const toggle = (list: string[], set: (v: string[]) => void, v: string) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const chip = (on: boolean) => on ? { background: c.accentBg, color: c.accentSoft, border: `1px solid ${c.accent}` } : { background: c.bgElevated, color: c.text, border: `1px solid ${c.border}` };
  return (
    <ModalShell title={`Who can see "${folder.name}"`} onClose={onClose}>
      {isReports ? (
        <p className="text-sm" style={{ color: c.textDim }}>Reports have sales numbers, so they stay with owners and managers.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            {[{ v: false, t: 'Everyone', d: parentLocked ? 'Everyone who can see the folder above' : 'The whole team' }, { v: true, t: 'Only some people', d: 'Pick teams or people' }].map((o) => (
              <button key={o.t} onClick={() => setRestricted(o.v)} className="text-left rounded-xl px-3 py-2.5" style={chip(restricted === o.v)}>
                <p className="text-sm font-semibold">{o.t}</p><p className="text-[11px] mt-0.5" style={{ color: c.textDim }}>{o.d}</p>
              </button>
            ))}
          </div>
          {restricted && (
            <>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: c.textFaint }}>Teams</p>
                <div className="flex flex-wrap gap-2">
                  {TEAMS.map((t) => <button key={t.role} onClick={() => toggle(roles, setRoles, t.role)} className="h-9 px-3 rounded-full text-[13px] font-semibold" style={chip(roles.includes(t.role))}>{roles.includes(t.role) && <Check size={12} className="inline mr-1 -mt-0.5" />}{t.label}</button>)}
                </div>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: c.textFaint }}>People</p>
                {people.length === 0 ? <p className="text-sm" style={{ color: c.textDim }}>No team members with a login yet.</p> : (
                  <div className="flex flex-wrap gap-2">
                    {people.map((u) => {
                      const viaTeam = roles.includes(u.app_role);
                      return (
                        <button key={u.id} disabled={viaTeam} onClick={() => toggle(userIds, setUserIds, u.id)} className="h-9 px-3 rounded-full text-[13px] font-semibold disabled:opacity-60" style={chip(viaTeam || userIds.includes(u.id))}>
                          {(viaTeam || userIds.includes(u.id)) && <Check size={12} className="inline mr-1 -mt-0.5" />}{u.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <p className="text-[12px]" style={{ color: c.textDim }}>Owners and managers can always see every folder. Locking a folder also locks everything inside it, and OpsAlo's AI won't use these files to answer anyone else.</p>
            </>
          )}
          {err && <p className="text-sm" style={{ color: c.danger }}>{err}</p>}
          <button disabled={busy} onClick={async () => { setBusy(true); const r = await onSave(restricted, roles, userIds.filter((id) => !roles.includes(people.find((p) => p.id === id)?.app_role ?? ''))); setBusy(false); if (r) setErr(r); }}
            className="w-full h-11 rounded-xl text-sm font-semibold disabled:opacity-50" style={{ background: c.accent, color: '#fff' }}>{busy ? 'Saving…' : 'Save'}</button>
        </>
      )}
    </ModalShell>
  );
}

// Reports: weekly and nightly PDFs, newest first. Filled automatically: nightly when the numbers are submitted,
// weekly every Monday morning. Each owner/manager can turn on getting them by email.
function ReportsView({ venueId }: { venueId: string }) {
  const c = useAppColors();
  const [rows, setRows] = useState<Array<{ trade_date: string; pdf_path: string }> | null>(null);
  const [weeks, setWeeks] = useState<Array<{ week_start: string; pdf_path: string | null; totals: { total?: number; covers?: number } | null }>>([]);
  const [sendTo, setSendTo] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const loadWeeks = async () => {
    const { data } = await supabase.from('weekly_reports').select('week_start, pdf_path, totals').eq('venue_id', venueId).order('week_start', { ascending: false }).limit(60);
    setWeeks((data ?? []) as typeof weeks);
  };
  useEffect(() => {
    void supabase.from('sales_days').select('trade_date, pdf_path').eq('venue_id', venueId).not('pdf_path', 'is', null).order('trade_date', { ascending: false }).limit(400)
      .then(({ data, error }) => { if (error) setErr(error.message); setRows((data ?? []) as Array<{ trade_date: string; pdf_path: string }>); });
    void loadWeeks();
    void supabase.from('report_recipients').select('id', { count: 'exact', head: true }).eq('venue_id', venueId)
      .then(({ count, error }) => setSendTo(error ? null : count ?? 0));
  }, [venueId]); // eslint-disable-line react-hooks/exhaustive-deps
  const makeNow = async () => {
    setMaking(true); setErr(null);
    const { data, error } = await supabase.functions.invoke('weekly-report', { body: {} });
    setMaking(false);
    const e = error?.message ?? (data as { error?: string; skipped?: string } | null)?.error ?? (data as { skipped?: string } | null)?.skipped;
    if (e) { setErr(e); return; }
    await loadWeeks();
  };
  const short = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  const plus6 = (iso: string) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + 6); return d.toISOString().slice(0, 10); };
  if (rows === null) return <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin" style={{ color: c.textFaint }} /></div>;
  const months = new Map<string, typeof rows>();
  for (const r of rows) { const k = r.trade_date.slice(0, 7); months.set(k, [...(months.get(k) ?? []), r]); }
  return (
    <div className="space-y-5">
      {sendTo != null && (
        <p className="text-[13px]" style={{ color: c.textDim }}>
          {sendTo ? `Emailed to ${sendTo} ${sendTo === 1 ? 'person' : 'people'}.` : 'Not emailed to anyone yet.'} Change who gets them in Settings › Report emails.
        </p>
      )}
      {err && <p className="text-sm" style={{ color: c.danger }}>{err}</p>}

      <section className="space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.06em]" style={{ color: c.textDim }}>Weekly</h2>
          <button onClick={() => void makeNow()} disabled={making} className="h-8 px-3 rounded-lg text-[12px] font-semibold flex items-center gap-1.5 disabled:opacity-60" style={{ background: c.iconBg, color: c.text }}>
            {making ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {making ? 'Making it…' : "Make last week's now"}
          </button>
        </div>
        {weeks.length === 0 ? (
          <p className="text-sm rounded-2xl p-4" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.textDim }}>The first weekly report arrives on Monday morning, for Monday to Sunday.</p>
        ) : (
          <div className="rounded-2xl overflow-hidden" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
            {weeks.map((w, i) => (
              <button key={w.week_start} disabled={!w.pdf_path} onClick={async () => { if (w.pdf_path) { const e = await openEodPdf(w.pdf_path); if (e) setErr(e); } }}
                className="w-full text-left flex items-center gap-3.5 px-4 py-3" style={{ borderTop: i ? `1px solid ${c.border}` : undefined }}>
                <FileText size={18} style={{ color: c.accentSoft }} />
                <span className="flex-1 text-[15px] font-semibold" style={{ color: c.text }}>Week of {short(w.week_start)} – {short(plus6(w.week_start))}</span>
                <span className="text-[13px]" style={{ color: c.textDim }}>{w.totals?.total ? `${formatAud(w.totals.total)} · ${w.totals.covers ?? 0} covers` : 'PDF'}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {rows.length === 0 ? (
        <div className="rounded-2xl p-10 text-center" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
          <p className="text-base font-semibold" style={{ color: c.text }}>No nightly reports yet</p>
          <p className="text-sm mt-1" style={{ color: c.textDim }}>Each night's report lands here as a PDF when the numbers are submitted.</p>
        </div>
      ) : [...months.entries()].map(([m, list]) => (
        <section key={m} className="space-y-2.5">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.06em]" style={{ color: c.textDim }}>
            {new Date(`${m}-15T12:00:00`).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })} · Nightly
          </h2>
          <div className="rounded-2xl overflow-hidden" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
            {list.map((r, i) => (
              <button key={r.trade_date} onClick={async () => { const e = await openEodPdf(r.pdf_path); if (e) setErr(e); }}
                className="w-full text-left flex items-center gap-3.5 px-4 py-3" style={{ borderTop: i ? `1px solid ${c.border}` : undefined }}>
                <FileText size={18} style={{ color: c.gold }} />
                <span className="flex-1 text-[15px] font-semibold" style={{ color: c.text }}>
                  {new Date(`${r.trade_date}T12:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                </span>
                <span className="text-[13px]" style={{ color: c.textDim }}>Nightly report · PDF</span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/* ---------- pieces ---------- */

function useUseLine() {
  return (use: Usage | undefined) => {
    if (!use) return null;
    if (use.courses.length) return `Used in ${use.courses[0].title}${use.courses.length > 1 ? ` +${use.courses.length - 1}` : ''}`;
    if (use.tasks.length) return `Linked to ${use.tasks.length} task${use.tasks.length === 1 ? '' : 's'}`;
    return null;
  };
}

function ExtBadge({ ext, size = 40 }: { ext: string; size?: number }) {
  const c = useAppColors();
  const d = c.isDark;
  const tone = ext === 'pdf' ? { bg: d ? 'rgba(248,113,113,0.14)' : '#FCE8E4', fg: d ? '#FCA5A5' : '#9A2A1C' }
    : isImage(ext) ? { bg: d ? 'rgba(232,160,32,0.15)' : '#FCEFD9', fg: d ? '#FCD38D' : '#8A4B08' }
    : isVideo(ext) ? { bg: d ? 'rgba(167,139,250,0.16)' : '#EEE8FA', fg: d ? '#C4B5FD' : '#5A3F8C' }
    : { bg: d ? 'rgba(96,165,250,0.15)' : '#E3EEF8', fg: d ? '#9CC7F0' : '#1F4E79' };
  return (
    <span className="rounded-[10px] flex items-center justify-center font-extrabold shrink-0" aria-hidden="true"
      style={{ width: size, height: size, fontSize: size > 36 ? 11 : 10, background: tone.bg, color: tone.fg }}>
      {(ext || 'file').slice(0, 4).toUpperCase()}
    </span>
  );
}

function AiBadge({ status }: { status?: string }) {
  const c = useAppColors();
  if (status === 'ready') return <span className="text-[12px] font-semibold flex items-center gap-1" style={{ color: c.accentSoft }}><Check size={13} strokeWidth={3} />AI has read it</span>;
  if (status === 'processing') return <span className="text-[12px] font-semibold flex items-center gap-1" style={{ color: c.textDim }}><Loader2 size={12} className="animate-spin" />Reading…</span>;
  if (status === 'failed') return <span className="text-[12px] font-semibold flex items-center gap-1" style={{ color: c.danger }}><AlertTriangle size={12} />Couldn't read</span>;
  return null;
}

function DocRow({ d, first, ai, use, onOpen, where }: { d: Doc; first: boolean; ai?: string; use?: Usage; onOpen: () => void; where?: string | null }) {
  const c = useAppColors();
  const line = useUseLine()(use);
  return (
    <button onClick={onOpen} className="w-full text-left flex items-center gap-3.5 px-4 py-3.5" style={{ borderTop: first ? undefined : `1px solid ${c.border}` }}>
      <ExtBadge ext={d.ext} size={36} />
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] font-semibold truncate" style={{ color: c.text }}>{d.label}</span>
        <span className="block text-[13px] truncate" style={{ color: c.textDim }}>{[where, docTypeLabel(d.type), line].filter(Boolean).join(' · ') || ' '}</span>
      </span>
      <span className="hidden sm:block"><AiBadge status={ai} /></span>
      <span className="text-[13px] shrink-0" style={{ color: c.textDim }}>{shortDate(d.updated_at)}</span>
    </button>
  );
}

function SideLink({ label, n, on, onClick, warn, lock }: { label: string; n?: number; on: boolean; onClick: () => void; warn?: boolean; lock?: boolean }) {
  const c = useAppColors();
  return (
    <button onClick={onClick} className="flex justify-between items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-left"
      style={{ background: on ? c.iconBg : 'transparent', color: warn ? (c.isDark ? '#FCD38D' : '#8A4B08') : c.text, fontWeight: on || warn ? 700 : 500 }}>
      <span className="truncate flex items-center gap-1.5">{lock && <Lock size={12} style={{ color: c.textDim }} />}{label}</span>
      {n != null && <span className="text-[13px]" style={{ color: warn ? undefined : c.textDim }}>{n}</span>}
    </button>
  );
}

function UploadModal({ initialType, onClose, onUpload, into }: { initialType: string; onClose: () => void; onUpload: (f: File[], type: string) => Promise<boolean>; into?: string | null }) {
  const c = useAppColors();
  const [type, setType] = useState(initialType);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const go = async (files: File[]) => {
    if (!files.length) return;
    setBusy(true);
    const ok = await onUpload(files, type);
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <>
      <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-[60]" onClick={() => !busy && onClose()} />
      <div role="dialog" aria-label="Upload documents" className="modal-panel fixed inset-x-4 lg:inset-x-auto lg:left-1/2 lg:-translate-x-1/2 lg:w-[560px] top-10 max-h-[calc(100vh-5rem)] rounded-2xl z-[60] flex flex-col overflow-hidden" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
        <header className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${c.border}` }}>
          <h2 className="text-base font-bold" style={{ color: c.text }}>Upload documents{into ? <span className="font-normal" style={{ color: c.textDim }}> to {into}</span> : null}</h2>
          <button onClick={onClose} disabled={busy} className="p-2 -m-1" style={{ color: c.textDim }} aria-label="Close"><X size={18} /></button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <p className="text-sm font-semibold mb-2" style={{ color: c.text }}>What is it?</p>
            <div className="grid grid-cols-2 gap-2">
              {DOC_TYPES.map((t) => (
                <button key={t.id} onClick={() => setType(t.id)} title={t.hint} className="text-left px-3 py-2.5 rounded-xl text-[13px]"
                  style={type === t.id ? { background: c.accentBg, color: c.accentSoft, border: `2px solid ${c.accent}`, fontWeight: 700 } : { background: c.bgElevated, color: c.text, border: `1px solid ${c.border}`, fontWeight: 500 }}>
                  {t.label}
                </button>
              ))}
            </div>
            <p className="text-[12px] mt-2" style={{ color: c.textDim }}>{DOC_TYPES.find((t) => t.id === type)?.hint}</p>
          </div>
          <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); void go(Array.from(e.dataTransfer.files)); }}
            className="rounded-2xl p-6 flex flex-col items-center gap-3 text-center" style={{ border: `2px dashed ${drag ? c.accent : c.borderStrong}`, background: drag ? c.accentBg : 'transparent' }}>
            <input ref={ref} type="file" multiple className="hidden" accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,image/*,video/*" onChange={(e) => void go(Array.from(e.target.files ?? []))} />
            <p className="text-sm" style={{ color: c.textDim }}>Drop files here, or</p>
            <button onClick={() => ref.current?.click()} disabled={busy} className="h-11 px-5 rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-60" style={{ background: c.accent, color: '#fff' }}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {busy ? 'Uploading…' : 'Choose files'}
            </button>
            <p className="text-[12px]" style={{ color: c.textDim }}>PDFs and clear photos work best. Up to 10MB each.</p>
          </div>
        </div>
      </div>
    </>
  );
}

function DocDetail({ doc, venueId, canManage, hook, ai, usage, onBack, onType, onReplaced, onDeleted, onRenamed, folders, folderLabel, onMove, editPreset, onPresetUsed }: {
  doc: Doc; venueId: string; canManage: boolean; hook: ReturnType<typeof useFileUpload>;
  editPreset?: PdfChange[] | null; onPresetUsed?: () => void;
  folders: DocFolder[]; folderLabel: (id: string | null) => string; onMove: (folderId: string | null) => Promise<string | null>;
  ai: { status: string; text: string | null } | null; usage: Usage;
  onBack: () => void; onType: (t: string) => void; onReplaced: (f: File) => Promise<string | null>; onDeleted: () => Promise<void>; onRenamed: (n: string) => Promise<string | null>;
}) {
  const c = useAppColors();
  const [url, setUrl] = useState<string | null>(null);
  const [ask, setAsk] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [quickEdit, setQuickEdit] = useState(false);
  const [preset, setPreset] = useState<PdfChange[] | null>(null);
  useEffect(() => { if (editPreset?.length && doc.ext === 'pdf' && canManage) { setPreset(editPreset); setQuickEdit(true); onPresetUsed?.(); } }, [editPreset]); // eslint-disable-line react-hooks/exhaustive-deps
  const replaceRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    let off = false;
    setUrl(null);
    void hook.getSignedUrl(doc.path).then((r) => { if (!off) setUrl(r.url); });
    return () => { off = true; };
  }, [doc.path]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phones and the iOS app draw the PDF to fit the width; desktop browsers keep their own PDF viewer.
  const [fitPdf] = useState(() => window.innerWidth < 1024 || document.documentElement.classList.contains('cap-native'));
  const askAi = (text: string) => window.dispatchEvent(new CustomEvent('opsalo:ask', { detail: { text } }));
  const ref = `"${doc.label}" (${doc.bucket}/${doc.name})`;
  const excerpt = ai?.status === 'ready' && ai.text ? ai.text.replace(/[#*_>|-]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 260) : null;
  void venueId;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
      className="px-4 sm:px-8 py-6 max-w-6xl mx-auto w-full space-y-5 pb-24">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onBack} aria-label="Back to documents" className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
            <ChevronLeft size={18} />
          </button>
          <div className="min-w-0">
            {renaming != null ? (
              <form onSubmit={async (e) => { e.preventDefault(); setBusy('rename'); const r = await onRenamed(renaming); setBusy(null); if (r) setErr(r); else setRenaming(null); }} className="flex gap-2">
                <input autoFocus value={renaming} onChange={(e) => setRenaming(e.target.value)} aria-label="New name"
                  className="h-10 px-3 rounded-xl text-lg font-bold outline-none min-w-0" style={{ background: c.bgElevated, border: `1px solid ${c.borderStrong}`, color: c.text }} />
                <button className="h-10 px-3 rounded-xl text-sm font-semibold" style={{ background: c.accent, color: '#fff' }}>{busy === 'rename' ? '…' : 'Save'}</button>
                <button type="button" onClick={() => setRenaming(null)} className="h-10 px-3 rounded-xl text-sm" style={{ color: c.textDim }}>Cancel</button>
              </form>
            ) : (
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2 min-w-0" style={{ color: c.text }}>
                <span className="truncate">{doc.label}</span>
                {canManage && <button onClick={() => setRenaming(doc.label)} aria-label="Rename" className="p-1 rounded-md shrink-0" style={{ color: c.textDim }}><Pencil size={15} /></button>}
              </h1>
            )}
            <p className="text-[13px]" style={{ color: c.textDim }}>{doc.ext.toUpperCase()}{doc.size ? ` · ${doc.size < 1024 * 1024 ? `${Math.max(1, Math.round(doc.size / 1024))} KB` : `${(doc.size / 1024 / 1024).toFixed(1)} MB`}` : ''}{doc.updated_at ? ` · updated ${shortDate(doc.updated_at)}` : ''}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <a href={url ?? undefined} target="_blank" rel="noopener" download className="h-10 px-3.5 rounded-xl text-sm font-semibold flex items-center gap-2" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text, opacity: url ? 1 : 0.5 }}>
            <Download size={15} /> Download
          </a>
          {canManage && (
            <>
              {doc.ext === 'pdf' && (
                <button onClick={() => setQuickEdit(true)} disabled={!url} className="h-10 px-3.5 rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-50" style={{ background: c.accent, color: '#fff' }} title="Change a price or a few words, keeping the layout">
                  <PenLine size={15} /> Quick edit
                </button>
              )}
              <input ref={replaceRef} type="file" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; setBusy('replace'); const r = await onReplaced(f); setBusy(null); if (r) setErr(r); }} />
              <button onClick={() => replaceRef.current?.click()} disabled={!!busy} className="h-10 px-3.5 rounded-xl text-sm font-semibold flex items-center gap-2" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
                {busy === 'replace' ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Replace file
              </button>
            </>
          )}
        </div>
      </header>
      {err && <p className="text-sm" style={{ color: c.danger }}>{err}</p>}
      {quickEdit && url && (
        <PdfQuickEdit url={url} name={doc.name} preset={preset} onClose={() => { setQuickEdit(false); setPreset(null); }}
          onSave={async (file, mode) => {
            if (mode === 'replace') return await onReplaced(file);
            const r = await hook.upload(file);
            if (r.error) return r.error;
            await hook.reload();
            return null;
          }} />
      )}

      <div className="flex flex-col lg:flex-row gap-5 items-start">
        <div className={`order-2 lg:order-1 flex-1 min-w-0 w-full rounded-2xl overflow-hidden flex items-center justify-center ${fitPdf ? 'p-2' : ''}`}
          style={{ background: fitPdf ? c.iconBg : c.bgElevated, border: `1px solid ${c.border}`, height: fitPdf ? undefined : 'min(78vh, 820px)', minHeight: fitPdf ? 200 : undefined }}>
          {!url ? <Loader2 size={20} className="animate-spin" style={{ color: c.textFaint }} />
            : doc.ext === 'pdf' ? (fitPdf ? <PdfPages url={url} label={doc.label} /> : <iframe src={url} title={doc.label} className="w-full h-full" style={{ border: 0 }} />)
            : isImage(doc.ext) && doc.ext !== 'heic' ? <img src={url} alt={doc.label} className="max-w-full max-h-full object-contain" />
            : isVideo(doc.ext) ? <video src={url} controls className="max-w-full max-h-full" />
            : (
              <div className="text-center p-8 space-y-3">
                <ExtBadge ext={doc.ext} size={56} />
                <p className="text-sm" style={{ color: c.textDim }}>This file type can't be shown here.</p>
                <a href={url} target="_blank" rel="noopener" className="inline-flex h-10 px-4 rounded-xl text-sm font-semibold items-center gap-2" style={{ background: c.accent, color: '#fff' }}><Download size={15} /> Open it</a>
              </div>
            )}
        </div>

        <aside className="order-1 lg:order-2 w-full lg:w-[360px] shrink-0 space-y-3.5">
          <section className="rounded-2xl p-5 space-y-3" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
            <label className="block">
              <span className="block text-[11px] font-bold uppercase tracking-widest mb-1.5" style={{ color: c.textDim }}>Type</span>
              {canManage ? (
                <select value={doc.type ?? ''} onChange={(e) => onType(e.target.value)} className="w-full h-11 px-3 rounded-xl text-sm outline-none" style={{ background: c.iconBg, border: `1px solid ${c.border}`, color: c.text }}>
                  {!doc.type && <option value="">Choose a type</option>}
                  {DOC_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              ) : <span className="text-sm" style={{ color: c.text }}>{docTypeLabel(doc.type) ?? 'Not set'}</span>}
            </label>
            <label className="block">
              <span className="block text-[11px] font-bold uppercase tracking-widest mb-1.5" style={{ color: c.textDim }}>Folder</span>
              {canManage ? (
                <select value={doc.folderId ?? ''} onChange={async (e) => { setBusy('move'); const r = await onMove(e.target.value || null); setBusy(null); if (r) setErr(r); }}
                  disabled={busy === 'move'} className="w-full h-11 px-3 rounded-xl text-sm outline-none" style={{ background: c.iconBg, border: `1px solid ${c.border}`, color: c.text }}>
                  <option value="">Not in a folder</option>
                  {folders.map((f) => ({ f, label: folderLabel(f.id) })).sort((a, b) => a.label.localeCompare(b.label)).map(({ f, label }) => (
                    <option key={f.id} value={f.id}>{label}{f.restricted ? ' (locked)' : ''}</option>
                  ))}
                </select>
              ) : <span className="text-sm" style={{ color: c.text }}>{folderLabel(doc.folderId) || 'Not in a folder'}</span>}
            </label>
            <div>
              <span className="block text-[11px] font-bold uppercase tracking-widest mb-1.5" style={{ color: c.textDim }}>What the AI read</span>
              {ai?.status === 'ready' ? (
                <>
                  {excerpt && <p className="text-sm leading-relaxed" style={{ color: c.text }}>{excerpt}{(ai.text?.length ?? 0) > 260 ? '…' : ''}</p>}
                  <p className="text-[12px] font-semibold mt-1.5" style={{ color: c.accentSoft }}>Staff questions about this get answered from it.</p>
                </>
              ) : ai?.status === 'processing' ? (
                <p className="text-sm" style={{ color: c.textDim }}>Reading it now. This takes a minute or two.</p>
              ) : ai?.status === 'failed' ? (
                <p className="text-sm" style={{ color: c.danger }}>The AI couldn't read this file. Replace it with a clear PDF or photo.</p>
              ) : (
                <p className="text-sm" style={{ color: c.textDim }}>Waiting to be read. This starts by itself and takes a minute or two.</p>
              )}
            </div>
          </section>

          <section className="rounded-2xl p-5 space-y-2.5" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
            <span className="block text-[11px] font-bold uppercase tracking-widest" style={{ color: c.textDim }}>Ask about it</span>
            <form onSubmit={(e) => { e.preventDefault(); if (ask.trim()) askAi(`About the document ${ref}: ${ask.trim()}`); }}
              className="flex items-center gap-2 h-12 pl-3.5 pr-1.5 rounded-xl" style={{ background: c.iconBg, border: `1px solid ${c.border}` }}>
              <input value={ask} onChange={(e) => setAsk(e.target.value)} placeholder="Ask anything about this document" aria-label="Ask about this document"
                className="flex-1 min-w-0 bg-transparent outline-none text-sm" style={{ color: c.text }} />
              <button aria-label="Ask" disabled={!ask.trim()} className="h-9 w-9 rounded-lg flex items-center justify-center disabled:opacity-40" style={{ background: c.accent, color: '#fff' }}><ArrowRight size={16} /></button>
            </form>
          </section>

          {canManage && (
            <section className="rounded-2xl p-5 space-y-2.5" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
              <span className="block text-[11px] font-bold uppercase tracking-widest" style={{ color: c.textDim }}>Use it with the team</span>
              <button onClick={() => askAi(`Create a training quiz for the team from the document ${ref}.`)}
                className="w-full h-12 px-4 rounded-xl text-sm font-semibold flex items-center gap-2.5" style={{ background: c.accent, color: '#fff' }}>
                <GraduationCap size={16} /> Make a quiz from this
              </button>
              <button onClick={() => askAi(`Create a task asking the team to read the document ${ref}, linked to the document. Ask me who it's for and when it's due if you need to.`)}
                className="w-full h-12 px-4 rounded-xl text-sm font-semibold flex items-center gap-2.5" style={{ background: 'transparent', border: `1px solid ${c.borderStrong}`, color: c.text }}>
                <ListChecks size={16} /> Ask people to read it
              </button>
              {(usage.courses.length > 0 || usage.tasks.length > 0) && (
                <div className="pt-2 space-y-1.5" style={{ borderTop: `1px solid ${c.border}` }}>
                  <p className="text-[13px]" style={{ color: c.textDim }}>Used in</p>
                  {usage.courses.map((x) => (
                    <button key={x.id} onClick={() => window.dispatchEvent(new CustomEvent('opsalo:navigate', { detail: { page: 'training', courseId: x.id } }))}
                      className="w-full flex justify-between gap-2 text-sm text-left" style={{ color: c.text }}>
                      <span className="font-semibold truncate">{x.title}</span><span className="shrink-0" style={{ color: c.textDim }}>Quiz</span>
                    </button>
                  ))}
                  {usage.tasks.map((x) => (
                    <div key={x.id} className="flex justify-between gap-2 text-sm" style={{ color: c.text }}>
                      <span className="font-semibold truncate">{x.title}</span><span className="shrink-0" style={{ color: c.textDim }}>{x.total ? `${x.done} of ${x.total} done` : 'Task'}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[12px] flex items-start gap-1.5 pt-1" style={{ color: c.textDim }}><Sparkles size={13} className="mt-0.5 shrink-0" />These open a chat with the AI, which sets it up and checks with you first.</p>
            </section>
          )}

          {canManage && (
            <button onClick={async () => { if (!window.confirm(`Delete "${doc.label}"? Staff and the AI won't be able to use it any more.`)) return; setBusy('delete'); await onDeleted(); }}
              disabled={!!busy} className="w-full h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2" style={{ color: c.danger }}>
              <Trash2 size={15} /> Delete document
            </button>
          )}
        </aside>
      </div>
    </motion.div>
  );
}
